"use client";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import { closeIcon, scrollDownIcon, sendIcon, uploadIcon } from "../../assets";
import {
  postComment,
  sendTypingEvent,
  updateComment,
  generateAbbreviation,
} from "../../lib/util";
import { useToken } from "../../lib/context/auth-context";
import MediaUploader from "../common/media-uploader";
import Skeleton from "react-loading-skeleton";
import toast from "react-hot-toast";

const ChatBox = ({
  chatAreaRef,
  loggedInUser,
  onNewMessage,
  onReplaceMessage,
  onMarkFailed,
  replyTo,
  setReplyTo,
  editMessage,
  setEditMessage,
  onUpdateMessage,
  scrollToMessage,
  chatPayload = {},
  chatInfo = {},
  showScrollToBottom,
  presetMessage,
  clearPresetMessage,
  chatGuestMode,
}) => {
  const { sectionId, sectionName } = chatInfo;
  const { userId } = chatPayload;
  const [newMessage, setNewMessage] = useState("");
  const [hasPreset, setHasPreset] = useState(!!presetMessage);
  const [error, setError] = useState("");
  const { token } = useToken();
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [finishedUploading, setFinishedUploading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [loadedPresetImage, setLoadedPresetImage] = useState(false);

  const inputRef = useRef(null);


  const handleMediaUploadSuccess = (mediaData) => {
    setError("");
    setUploadedMedia(mediaData);
  };

  useEffect(() => {
    if (editMessage) {
      setNewMessage(editMessage.text);
      setReplyTo(null);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
          }
        });
      });
    }
  }, [editMessage]);

  useEffect(() => {
    if (replyTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyTo]);

  const uuid = () =>
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  const mapMedia = (media = []) =>
    Array.isArray(media)
      ? media.map((m) => ({
          mediaType: m.mediaType || "image",
          mediaThumb: m.mediaThumb || "",
          mediaUrl: m.mediaUrl || "",
          mediaExt: m.mediaExt || ".jpg",
          mediaHeight: m.mediaHeight || "",
          mediaWidth: m.mediaWidth || "",
          mediaName: m.mediaName || "",
          mediaTime: m.mediaTime || new Date().toISOString(),
        }))
      : [];

  const sendComment = async (e) => {
    e.preventDefault();

    if (isUploading) {
      toast.error("Please wait for uploads to finish");
      return;
    }

    if (editMessage && !newMessage.trim() && uploadedMedia.length === 0) {
      toast.error("Message cannot be empty");
      return;
    }

    if (
      !editMessage &&
      !newMessage.trim() &&
      uploadedMedia.length === 0 &&
      !hasPreset
    ) {
      return;
    }

    if (editMessage) {
      const oldText = editMessage.text;

      onUpdateMessage(editMessage.trackId, (m) => ({
        ...m,
        comment: newMessage,
        commentEdit: true,
        sending: true,
        failed: false,
      }));

      try {
        const res = await updateComment(
          editMessage.trackId,
          { comment: newMessage },
          token
        );
        if (res.success) {
          onReplaceMessage(editMessage.trackId, res.data.comment);
          scrollToMessage(editMessage.trackId);
          resetMessage();
        } else {
          toast.error("Failed to update message");
          onUpdateMessage(editMessage.trackId, (m) => ({
            ...m,
            comment: oldText,
            sending: false,
            failed: true,
          }));
        }
      } catch (err) {
        toast.error("Failed to update message");
        onUpdateMessage(editMessage.trackId, (m) => ({
          ...m,
          comment: oldText,
          sending: false,
          failed: true,
        }));
      }
      return;
    }

    // const trackId = uuid();

    const replyObject = replyTo
      ? {
          trackId: replyTo.trackId,
          previewAuthor: replyTo.author?.name,
          comment: replyTo.comment,
          previewImage: replyTo.media?.[0]?.mediaThumb,
          userId: replyTo.userId,
          previewContent: replyTo.comment,
        }
      : null;

    const sendSingleComment = async (commentPayload) => {
      const trackId = uuid();

      commentPayload.trackId = trackId;
      const optimisticMsg = {
        ...commentPayload,
        trackId,
        author: chatGuestMode
          ? {
              name: `Guest-${userId?.slice(-4)}`,
              image: "",
              email: "",
            }
          : {
              name:
                `${loggedInUser?.user?.firstName ?? ""} ${
                  loggedInUser?.user?.lastName ?? ""
                }`.trim() || "Unknown",
              image: loggedInUser?.user?.image || "",
              email: loggedInUser?.user?.email || "",
            },
        reference: replyObject,
        createdAt: new Date().toISOString(),
        sending: true,
        retryCount: 0,
      };

      onNewMessage(optimisticMsg);
      setNewMessage("");
      setUploadedMedia([]);
      setReplyTo(null);
      setError("");
      clearPresetMessage?.();

      setFinishedUploading(true);
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
        inputRef.current.focus();
      }

      if (!editMessage && chatAreaRef.current) {
        requestAnimationFrame(() => {
          chatAreaRef.current.scrollTo({
            top: chatAreaRef.current.scrollHeight,
            behavior: "smooth",
          });
        });
      }

      try {
        const res = await postComment(
          commentPayload,
          sectionId,
          sectionName,
          token
        );
        if (res.success) {
          onReplaceMessage(trackId, res.data);
        } else {
          onMarkFailed(trackId);
        }
      } catch (err) {
        console.error("Failed to send:", err);
        onMarkFailed(trackId);
      }
    };
    try {
      // 1️⃣ Send product message first — wait until it's done (success OR fail)
      if (hasPreset && presetMessage) {
        const presetPayload = {
          ...chatPayload,
          ...presetMessage.apiPayload,
          expire: false,
        };
        setHasPreset(false);
        await sendSingleComment(presetPayload); // wait here before proceeding
      }

      // 2️⃣ Then send user’s typed message (if any)
      if (newMessage.trim() || uploadedMedia.length > 0) {
        const payload = {
          ...chatPayload,
          commentType: "MESSAGE",
          expire: false,
        };
        if (newMessage) payload.comment = newMessage;
        if (replyObject) payload.reference = replyObject;
        if (uploadedMedia.length > 0) payload.media = mapMedia(uploadedMedia);

        await sendSingleComment(payload);
      }
    } catch (err) {
      console.error("Send sequence failed:", err);
      toast.error("Something went wrong while sending messages.");
    }
  };

  const typingTimeoutRef = useRef(null);

  const handleTyping = () => {
    sendTypingEvent(
      "typing",
      loggedInUser?.user,
      sectionId,
      sectionName,
      token
    );
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingEvent(
        "stop-typing",
        loggedInUser?.user,
        sectionId,
        sectionName,
        token
      );
    }, 1000);
  };

  const cancelEdit = () => {
    if (editMessage) {
      onUpdateMessage(editMessage.trackId, (m) => ({
        ...m,
        sending: false,
        failed: false,
      }));
    }
    resetMessage();
  };

  const resetMessage = (keepFocus = false) => {
    setEditMessage(null);
    setNewMessage("");
    setUploadedMedia([]);
    setError("");
    setReplyTo(null);
    setFinishedUploading(false);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
        if (keepFocus) {
          inputRef.current.focus();
        } else {
          inputRef.current.blur();
        }
      }
    });
  };

  const handleInput = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendComment(e);
    }
  };

  const scrollToBottom = () => {
    if (!chatAreaRef.current) return;
    chatAreaRef.current.scrollTo({
      top: chatAreaRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!inputRef.current) return;

    const el = inputRef.current;
    const handleFocus = () => {
      if (typeof window !== "undefined" && "ontouchstart" in window) {
        setTimeout(() => {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 300);
      }
    };

    el.addEventListener("focus", handleFocus);
    return () => el.removeEventListener("focus", handleFocus);
  }, []);

  return (
    <form
      className={`${styles.chatInput} ${isFocused ? styles.focused : ""}`}
      onSubmit={sendComment}
    >
      {error && <div className={styles.chatErrorDisplay}>{error}</div>}

      {replyTo && (
        <div className={styles.replyBanner}>
          <span className={styles.replyAuthorView}>{replyTo.author?.name}</span>
          <p className={styles.replyText}>
            {replyTo.comment?.slice(0, 50) || "Media"}
          </p>
          {replyTo?.media?.[0] && (
            <div>
              <img
                src={replyTo?.media?.[0].mediaThumb}
                alt="Replied"
                style={{ width: "30px", height: "30px" }}
              />
            </div>
          )}
          <button
            onClick={() => setReplyTo(null)}
            className={styles.cancelReply}
          >
            {closeIcon}
          </button>
        </div>
      )}

      {editMessage && (
        <div className={styles.editBanner}>
          <button onClick={cancelEdit} className={styles.cancelEdit}>
            {closeIcon}
          </button>
        </div>
      )}

      {hasPreset && (
        <div className={styles.presetBanner}>
          <div className={styles.presetImage}>
            {presetMessage?.uiPreview.image ? (
              <>
                {!loadedPresetImage && <Skeleton width={60} height={60} />}
                <img
                  src={presetMessage?.uiPreview.image.mediaThumb}
                  alt={presetMessage?.uiPreview.title || "Product image"}
                  onLoad={() => setLoadedPresetImage(true)}
                  className={!loadedPresetImage ? "hidden" : ""}
                  style={{
                    width: 55,
                    height: 55,
                    objectFit: "cover",
                  }}
                />
              </>
            ) : (
              <div
                className="abbrevated-div"
                style={{
                  width: 55,
                  height: 55,
                  fontSize: "1.6rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f0f0f0",
                  borderRadius: "8px",
                }}
              >
                {generateAbbreviation(presetMessage?.uiPreview.title)}
              </div>
            )}
          </div>

          <div className={styles.presetInfo}>
            <div className={styles.presetTitle}>
              {presetMessage?.uiPreview.title}
            </div>
            <div className={styles.presetPrice}>
              {presetMessage?.uiPreview.price}
            </div>
            {presetMessage?.uiPreview.description && (
              <p className={styles.presetDescription}>
                {presetMessage?.uiPreview.description.slice(0, 80)}...
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setHasPreset(false)}
            className={styles.cancelPreset}
          >
            {closeIcon}
          </button>
        </div>
      )}

      <div className={styles.inputDiv} id="inputDiv">
        <div className={styles.inputArea}>
          <textarea
            id="chatMessage"
            ref={inputRef}
            value={newMessage}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              sendTypingEvent(
                "stop-typing",
                loggedInUser?.user,
                sectionId,
                sectionName,
                token
              );
            }}
            disabled={!loggedInUser?.user && !chatGuestMode}
            placeholder={
              !loggedInUser?.user && !chatGuestMode
                ? "Login required to chat"
                : chatGuestMode
                ? "Type a message as Guest..."
                : "Type a message..."
            }
            rows={1}
            className={styles.chatTextarea}
          />

          {!editMessage && (
            <div
              className={`mediaButton ${
                !loggedInUser?.user && !chatGuestMode ? "disabled" : ""
              }`}
            >
              <label htmlFor="media">
                <span>{uploadIcon}</span>
              </label>
            </div>
          )}
        </div>

        <div className={styles.uploadArea}>
          <MediaUploader
            onSuccess={handleMediaUploadSuccess}
            setError={setError}
            setIsUploading={setIsUploading}
            supportedFiles={["image/*,video/*,application/pdf"]}
            allowMultipleUpload={true}
            destination="attachments"
            finishedUploading={finishedUploading}
            presetDocuments={uploadedMedia}
            mediaThumbnailClass="media-container chat"
            mediaButtonClass="media-input hide"
            uploadText=""
          />
        </div>
      </div>

      <button
        disabled={
          (!loggedInUser?.user && !chatGuestMode) ||
          isUploading ||
          (!newMessage.trim() && uploadedMedia.length === 0 && !hasPreset)
        }
        type="submit"
        className={styles.sendBtn}
      >
        {sendIcon}
      </button>

      {showScrollToBottom && (
        <button className={styles.scrollToBottomBtn} onClick={scrollToBottom}>
          {scrollDownIcon}
        </button>
      )}
    </form>
  );
};

export default ChatBox;
